import React from 'react';

import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { ProgressBar } from '../../components/ProgressBar';
import { currencyFormatter } from '../../components/shared/numberFormat';
import type { Account } from '../../generated-types';
import type { SalaryDetailsResponse } from '../../hooks/useNextSalary';
import useTranslation, { format, formatJsx } from '../../hooks/useTranslation';
import SalaryInfoHeader from './SalaryInfoHeader';

interface Props {
    salaryDetails: SalaryDetailsResponse;
    salaryAccount: Account;
}

const SalaryInfoContent: React.FC<Props> = ({ salaryAccount, salaryDetails: { amountLeft, daysLeft } }) => {
    const translated = useTranslation();

    return (
        <>
            <SalaryInfoHeader />
            <div className="salary-info-card">
                <RichIcon type={RichIconTypes.NESTE_LOENNING} maxHeight={124} minHeight={124} />
                <div className="salary-info__amount-container">
                    <div className="salary-info__account" data-testid="Salary-info-account">
                        {salaryAccount?.alias}
                    </div>
                    <div className="salary-info__next-salary">{translated.salaryInfo.untilNextSalary}</div>
                    <div className="salary-info__amount" data-testid="Salary-info-amount">
                        {currencyFormatter(amountLeft, 'NOK', 0)}
                    </div>
                </div>
                <div className="salary-info__progress-container">
                    <ProgressBar
                        className="salary-info__progress-bar"
                        label={
                            <span className="salary-info__progress-text">
                                {formatJsx(
                                    translated.salaryInfo.payments,
                                    <strong>
                                        {format(
                                            translated.salaryInfo.left,
                                            currencyFormatter(
                                                salaryAccount.balance.availableBalance - amountLeft,
                                                'NOK',
                                                0,
                                            ),
                                        )}
                                    </strong>,
                                )}
                            </span>
                        }
                        isWarning={
                            salaryAccount.balance.availableBalance - amountLeft >=
                            salaryAccount.balance.availableBalance * 0.8
                        }
                        isDanger={
                            salaryAccount.balance.availableBalance - amountLeft >=
                            salaryAccount.balance.availableBalance * 0.99
                        }
                        max={salaryAccount.balance.availableBalance}
                        value={Math.max(
                            salaryAccount.balance.availableBalance - amountLeft,
                            salaryAccount.balance.availableBalance / 44,
                        )}
                    />
                    <ProgressBar
                        label={
                            <span className="salary-info__progress-text">
                                {translated.salaryInfo.untilSalary}
                                <strong>{format(translated.salaryInfo.daysLeft, daysLeft)}</strong>
                            </span>
                        }
                        show={!!daysLeft}
                        wrapperClassName="salary-info__progress-bar__wrapper"
                        className="salary-info__progress-bar"
                        value={32 - daysLeft}
                        max={32}
                    />
                </div>
            </div>
        </>
    );
};

export default SalaryInfoContent;
